import { useState, useMemo, useCallback, useEffect } from "react";
import { useWindowSize } from "hooks/useWindowSize";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import {
  useGetRevenueScheduleQuery,
  RevenueSchedule,
  useLazyGetRevenueScheduleQuery,
} from "store/apis/revenueSchedule";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { Button } from "components/DesignSystem/Button/Button";
import { TableUI } from "components/design/TableUI";
import Table from "components/DesignSystem/Table/V2/Table";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
  getGroupedRowModel,
  DisplayColumnDef,
} from "react-table-8.10.7";
import classNames from "classnames";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { useFilters } from "hooks/useFilter";
import { Chip } from "components/DesignSystem/Chips/Chips";
import dayjs from "dayjs";
import { formatTimeToMonthYear } from "utils/formatDate";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { colors } from "constants/colors";
import { YYYY_MM_DD } from "constants/date";
import { Info } from "components/icons/Info";
import ToolTip from "components/design/toolTip";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { useToast } from "hooks/useToast";
import { useDownloadFile } from "hooks/useDownloadFile";
import {
  REVENUE_SCHEDULE_EXPORT_TYPES,
  REVENUE_SCHEDULE_EXPORT_TYPES_DISPLAY,
  REVENUE_SCHEDULE_EXPORT_TYPES_TYPE,
} from "constants/revenueScheduleExportTypes";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { CaretDown } from "components/icons/CaretDown";
import { useModal } from "hooks/useModal";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";
import { getCurrentDisplayValue } from "components/DesignSystem/Combobox/utils/getCurrentDisplayValue";
import { RevenueWaterfallDetailsModal } from "components/RevenueWaterfallDetails/RevenueWaterfallDetails";

type RevenueWaterfallRow = RevenueSchedule & {
  isTotal?: boolean;
};

const DESKTOP_PINNING = {
  left: ["period", "booked_revenue", "recognized_total", "deferred_total"],
};
// On phones the pinned columns would fill the screen, so everything scrolls together instead.
const NO_PINNING = { left: [] };

export const RevenueWaterfall = () => {
  const { isMobile } = useWindowSize();
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext().uuid;
  const [activeCellId, setActiveCellId] = useState<string | null>(null);

  const exportModal = useModal();

  const { values, updateFilter } = useFilters({
    initialValue: {
      START_DATE: dayjs().startOf("year").format(YYYY_MM_DD) as string,
      END_DATE: dayjs().endOf("month").format(YYYY_MM_DD) as string,
      SELECT_PERIOD: "CUSTOM" as string,
      INCLUDE_CARRY_FORWARD: false,
      SHOW_DEFERRALS: false,
    },
  });

  const {
    data: revenueScheduleData,
    isLoading: isLoadingRevenueSchedule,
    isFetching: isFetchingRevenueSchedule,
  } = useGetRevenueScheduleQuery(
    {
      entityId: entityId,
      start_date: values.START_DATE,
      end_date: values.END_DATE,
      carry_forward_invoices: values.INCLUDE_CARRY_FORWARD,
    },
    {
      skip: !entityId,
    }
  );

  const [
    lazyGetRevenueSchedule,
    { isLoading: isExporting, isFetching: isFetchingExport },
  ] = useLazyGetRevenueScheduleQuery();
  const { successToast, alertToast } = useToast();
  const {
    downloadFile,
    result: { isLoading: isDownloading, isFetching: isFetchingDownload },
  } = useDownloadFile();

  // Get all unique periods from periodic_recognized_revenue across all schedule items
  const allPeriods = useMemo(() => {
    if (!revenueScheduleData) return [];

    return revenueScheduleData.schedule.reduce<string[]>((acc, item) => {
      const periods = item.periodic_recognized_revenue.map(
        (period) => period.period
      );

      for (const period of periods) {
        if (!acc.includes(period)) {
          acc.push(period);
        }
      }
      return acc;
    }, []);
  }, [revenueScheduleData]);

  const startDate = revenueScheduleData?.schedule.at(0)?.period_start;

  // Prepare table data with totals row
  const tableData = useMemo((): RevenueWaterfallRow[] => {
    if (!revenueScheduleData) return [];

    const scheduleRows = revenueScheduleData.schedule.map((item) => ({
      ...item,
      isTotal: false,
    }));

    // Create totals row
    const totalsRow: RevenueWaterfallRow = {
      period: "Total",
      period_start: scheduleRows.at(0)?.period_start || "",
      period_end: scheduleRows.at(-1)?.period_end || "",
      booked_revenue: {
        booked_from_this_period_sum: revenueScheduleData.total_booked_revenue, // booked_invoice_ids
        booked_invoice_ids: [],
      },
      recognized_revenue: {
        recognized_from_this_month: 0,
        recognized_from_previous_months: 0,
        recognized_as_of_period_end:
          revenueScheduleData.total_recognized_revenue, // recognized_invoice_ids_as_of_current_period
        total_recognized: revenueScheduleData.total_recognized_revenue,
        recognized_invoice_ids_as_of_current_period: [],
      },
      deferred_revenue: {
        deferred_from_this_month: 0,
        deferred_from_previous_months: 0,
        prev_deferred_and_recognized_in_current_month: 0,
        deferred_revenue_as_of_period_end:
          revenueScheduleData.total_deferred_revenue_as_of_end_date, // deferred_invoice_ids_as_of_current_period
        total_deferred: revenueScheduleData.total_deferred_revenue,
        deferred_invoice_ids_as_of_current_period: [],
      },
      periodic_recognized_revenue: allPeriods.map((period) => {
        const currentSchedule = revenueScheduleData.schedule.find(
          (item) => period === item.period
        );

        return {
          period,
          period_start: currentSchedule?.period_start || "",
          period_end: currentSchedule?.period_end || "",
          recognized_revenue:
            currentSchedule?.recognized_revenue.total_recognized || 0,
          deferred_revenue:
            currentSchedule?.deferred_revenue.total_deferred || 0,
          recognized_invoice_ids: [],
          deferred_invoice_ids: [],
        };
      }),
      isTotal: true,
    };

    return [...scheduleRows, totalsRow];
  }, [revenueScheduleData, allPeriods]);

  const onOpen = useCallback((open: boolean, id: string) => {
    setActiveCellId(open ? id : null);
  }, []);

  const columnHelper = createColumnHelper<RevenueWaterfallRow>();

  const columns = useMemo(() => {
    let fixedColumns = [
      columnHelper.accessor("period", {
        header: "Month",
        cell: (info) => (
          <span
            className={
              info.row.original.isTotal ? "t-font-bold" : "t-font-normal"
            }
          >
            {info.getValue()}
          </span>
        ),
        size: 116,
      }),
      columnHelper.accessor("booked_revenue.booked_from_this_period_sum", {
        id: "booked_revenue",
        header: () => <div className="t-text-right">Booked</div>,
        cell: (info) => (
          <RevenueWaterfallDetailsModal
            onOpen={onOpen}
            invoiceStartDate={info.row.original.period_start}
            invoiceEndDate={info.row.original.period_end}
            recognitionStartDate={startDate}
            recognitionEndDate={info.row.original.period_end}
            recognitionType="BOOKED"
            header={`Revenue booked in ${info.row.original.period}`}
            id={info.cell.id}
          >
            <div className="t-text-right">
              <AmountSuperScript amount={Number(info.getValue())} />
            </div>
          </RevenueWaterfallDetailsModal>
        ),
        size: 116,
      }),
      columnHelper.accessor("recognized_revenue.recognized_as_of_period_end", {
        header: () => <div className="t-text-right">Recognised</div>,
        cell: (info) => (
          <RevenueWaterfallDetailsModal
            onOpen={onOpen}
            invoiceStartDate={info.row.original.period_start}
            invoiceEndDate={info.row.original.period_end}
            recognitionStartDate={info.row.original.period_start}
            recognitionEndDate={values.END_DATE}
            recognitionType="RECOGNIZED"
            header={`Revenue booked in ${
              info.row.original.period
            } and recognized as of ${
              info.row.original.periodic_recognized_revenue.at(-1)?.period
            }`}
            id={info.cell.id}
          >
            <div className="t-text-right">
              <AmountSuperScript amount={Number(info.getValue())} />
            </div>
          </RevenueWaterfallDetailsModal>
        ),
        size: 120,
        id: "recognized_total",
      }),
      ...(values.SHOW_DEFERRALS
        ? [
            columnHelper.accessor(
              "deferred_revenue.deferred_revenue_as_of_period_end",
              {
                header: () => <div className="t-text-right">Deferred</div>,
                cell: (info) => (
                  <RevenueWaterfallDetailsModal
                    onOpen={onOpen}
                    invoiceStartDate={info.row.original.period_start}
                    invoiceEndDate={info.row.original.period_end}
                    recognitionStartDate={startDate}
                    recognitionEndDate={info.row.original.period_end}
                    recognitionType="DEFERRED"
                    header={`Revenue booked in ${
                      info.row.original.period
                    } and recognized as of ${
                      info.row.original.periodic_recognized_revenue.at(-1)
                        ?.period
                    }`}
                    id={info.cell.id}
                  >
                    <div className="t-text-right">
                      <AmountSuperScript amount={Number(info.getValue())} />
                    </div>
                  </RevenueWaterfallDetailsModal>
                ),
                size: 116,
                id: "deferred_total",
              }
            ),
          ]
        : []),
    ];

    const asOfEndDateColumnGroup = columnHelper.group({
      id: "fixed_columns",
      header: () => (
        <div className="t-text-center t-text-text-60 t-text-subtext-sm">
          As of{" "}
          {formatTimeToMonthYear(
            revenueScheduleData?.schedule.at(-1)?.period_end
          )}
        </div>
      ),
      columns: fixedColumns as DisplayColumnDef<RevenueWaterfallRow, unknown>[],
    });

    // Add dynamic periodic columns with grouping
    const periodicColumnGroups = allPeriods.flatMap((period) => {
      const subColumns = [
        columnHelper.display({
          id: `period_${period}_recognized`,
          header: () => (
            <div className="t-text-right">
              {values.SHOW_DEFERRALS ? "Recognised" : period}
            </div>
          ),
          cell: (info) => {
            const periodicData =
              info.row.original.periodic_recognized_revenue.find(
                (p) => p.period === period
              );

            return periodicData ? (
              <RevenueWaterfallDetailsModal
                onOpen={onOpen}
                invoiceStartDate={info.row.original.period_start}
                invoiceEndDate={info.row.original.period_end}
                recognitionStartDate={periodicData.period_start}
                recognitionEndDate={periodicData.period_end}
                recognitionType="RECOGNIZED"
                header={`Revenue booked in ${info.row.original.period} and recognised in ${periodicData.period}`}
                id={info.cell.id}
              >
                <div className="t-text-right">
                  <AmountSuperScript
                    amount={Number(periodicData.recognized_revenue)}
                  />
                </div>
              </RevenueWaterfallDetailsModal>
            ) : (
              <></>
            );
          },
          size: 116,
        }),
      ];

      if (values.SHOW_DEFERRALS) {
        subColumns.push(
          columnHelper.display({
            id: `period_${period}_deferred`,
            header: () => <div className="t-text-right">Deferred</div>,
            cell: (info) => {
              const periodicData =
                info.row.original.periodic_recognized_revenue.find(
                  (p) => p.period === period
                );

              return (
                <div className="t-text-text-60">
                  {periodicData ? (
                    <RevenueWaterfallDetailsModal
                      onOpen={(open) =>
                        setActiveCellId(open ? info.cell.id : null)
                      }
                      invoiceStartDate={info.row.original.period_start}
                      invoiceEndDate={info.row.original.period_end}
                      recognitionStartDate={periodicData.period_start}
                      recognitionEndDate={periodicData.period_end}
                      recognitionType="DEFERRED"
                      header={`Revenue booked in ${info.row.original.period} and deferred in ${periodicData.period}`}
                      id={info.cell.id}
                    >
                      <div className="t-text-right">
                        <AmountSuperScript
                          amount={Number(periodicData.deferred_revenue)}
                        />
                      </div>
                    </RevenueWaterfallDetailsModal>
                  ) : (
                    "-"
                  )}
                </div>
              );
            },
            size: 116,
          })
        );

        return columnHelper.group({
          id: `period_group_${period}`,
          header: () => <div className="t-text-center">{period}</div>,
          columns: subColumns,
        });
      }

      return subColumns;
    });

    if (!values.SHOW_DEFERRALS) {
      const recognisedColumns = columnHelper.group({
        id: "period_group",
        header: () => (
          <div className="t-ml-40 t-text-text-60 t-text-subtext-sm">
            Recognised revenue
          </div>
        ),
        columns: periodicColumnGroups as DisplayColumnDef<
          RevenueWaterfallRow,
          unknown
        >[],
      });

      return {
        withoutDeferrals: [asOfEndDateColumnGroup, recognisedColumns],
        withDeferrals: [],
      };
    }

    return {
      withDeferrals: [asOfEndDateColumnGroup, ...periodicColumnGroups],
      withoutDeferrals: [],
    };
  }, [allPeriods, values.SHOW_DEFERRALS]);

  const table = useReactTable({
    columns: values.SHOW_DEFERRALS
      ? columns.withDeferrals
      : columns.withoutDeferrals,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 100 },
    getGroupedRowModel: getGroupedRowModel(),
    enableGrouping: true,
    state: {
      columnPinning: isMobile ? NO_PINNING : DESKTOP_PINNING,
    },
    enableColumnPinning: true,
  });

  const showBorderForStickyHeaders = (columnId: string) => {
    if (!values.SHOW_DEFERRALS) {
      return "period" === columnId;
    }

    return (
      "period" === columnId ||
      (columnId.startsWith("period_") && columnId.endsWith("_deferred"))
    );
  };

  const onExport = async ({
    startDate,
    endDate,
    showDeferrals,
    includeCarryForward,
    exportToMail,
    reportType,
  }: {
    startDate: string;
    endDate: string;
    showDeferrals: boolean;
    includeCarryForward: boolean;
    exportToMail: boolean;
    reportType: REVENUE_SCHEDULE_EXPORT_TYPES_TYPE;
  }) => {
    try {
      const downloadLazyGetRevenueSchedule = await lazyGetRevenueSchedule({
        entityId: entityId,
        start_date: startDate,
        end_date: endDate,
        carry_forward_invoices: includeCarryForward,
        export_to_mail: exportToMail,
        include_deferrals: showDeferrals,
        report_type: reportType,
        generate_report: true,
      }).unwrap();

      if (exportToMail) {
        successToast({
          message: "Report will be sent to your registered email shortly",
        });
        exportModal.close();
        return;
      }

      if (downloadLazyGetRevenueSchedule.document_id) {
        await downloadFile({
          file_id: downloadLazyGetRevenueSchedule.document_id,
          group_id: groupId,
        });
      }
      successToast({
        message: "Report exported successfully",
      });
      exportModal.close();
    } catch (error) {
      alertToast({
        message: "Failed to export report. Please try again.",
      });
    }
  };

  return (
    <div className="t-space-y-4">
      {/* Controls */}
      <div className="t-flex t-justify-between t-items-center sm:t-flex-wrap sm:t-items-center sm:t-gap-3">
        <div className="t-flex t-items-center t-gap-1 sm:t-order-1">
          <Filter.Root
            defaultValue="PERIOD"
            hideDivider={isMobile}
            title={
              isMobile
                ? values?.START_DATE && values?.END_DATE
                  ? "(1)"
                  : null
                : undefined
            }
            capsule={
              <span className="t-contents sm:t-hidden">
                {Boolean(values?.START_DATE) && Boolean(values?.END_DATE) && (
                  <Chip
                    onRemove={() => {}}
                    isRemovable={false}
                    isActive
                    filterType="PERIOD"
                  >
                    {formatTimeToMonthYear(values.START_DATE)}
                    {" to "}
                    {formatTimeToMonthYear(values.END_DATE)}
                  </Chip>
                )}
              </span>
            }
          >
            <Filter.Portal>
              <Filter.List>
                <Filter.ListItem value="PERIOD">Period</Filter.ListItem>
              </Filter.List>
              <Filter.Body value="PERIOD" block>
                <BooksDateFilter
                  pickerType="month"
                  values={values}
                  updateFilter={updateFilter}
                  skipMaxEndDate
                />
              </Filter.Body>
            </Filter.Portal>
          </Filter.Root>
        </div>

        <div className="t-flex t-items-center t-gap-6 sm:t-contents">
          <div className="t-flex t-items-center t-gap-2 sm:t-order-3 sm:t-w-full">
            <span className="t-text-body-sm t-text-text-60 t-flex t-items-center t-gap-1">
              <span>Include carry-forward from prior months</span>
              <ToolTip text="Includes revenue from older invoices that's still being recognised or deferred in the period selected.This gives a complete picture of total revenue activity each month.">
                <span className="t-flex">
                  <Info size="12" stroke="1.4" />
                </span>
              </ToolTip>
            </span>
            <Switch
              checked={values.INCLUDE_CARRY_FORWARD}
              onCheckedChange={(checked) =>
                updateFilter("INCLUDE_CARRY_FORWARD", checked)
              }
              size="small"
            />
          </div>

          <div className="t-flex t-items-center t-gap-2 sm:t-order-3 sm:t-w-full">
            <span className="t-text-body-sm t-text-text-60">
              Show deferrals
            </span>
            <Switch
              checked={values.SHOW_DEFERRALS}
              onCheckedChange={(checked) =>
                updateFilter("SHOW_DEFERRALS", checked)
              }
              size="small"
            />
          </div>

          <Modal.Root
            open={exportModal.isOpen}
            onOpenChange={exportModal.toggle}
          >
            <Modal.Trigger asChild>
              <Button size="xsmall" className="sm:t-order-2 sm:t-ml-auto">
                Export
              </Button>
            </Modal.Trigger>
            <Modal.Content useCustomOverlay>
              <FormikForm
                // @ts-ignore
                onSubmit={() => {}}
                initialValues={{
                  startDate: values.START_DATE,
                  endDate: values.END_DATE,
                  includeCarryForward: values.INCLUDE_CARRY_FORWARD,
                  showDeferrals: values.SHOW_DEFERRALS,
                  reportType: REVENUE_SCHEDULE_EXPORT_TYPES.MONTHLY,
                }}
              >
                {({ setFieldValue, values, submitForm }) => {
                  return (
                    <>
                      <Modal.Header>
                        <Modal.Title>
                          Export Revenue Recognition Report
                        </Modal.Title>
                        <Modal.Close />
                      </Modal.Header>
                      <Modal.Body className="t-flex t-flex-col t-gap-4">
                        <p className="t-mb-2 t-text-body-sm t-text-text-30">
                          Select the options below to customize and export the
                          report.
                        </p>
                        <DateField
                          name="startDate"
                          dateProps={{
                            label: "Start Date",
                            required: true,
                            showMonthYearPicker: true,
                          }}
                        />

                        <DateField
                          name="endDate"
                          dateProps={{
                            label: "End Date",
                            required: true,
                            showMonthYearPicker: true,
                            onDateChange: (date) => {
                              if (date) {
                                const endDate = dayjs(date)
                                  .endOf("month")
                                  .format(YYYY_MM_DD);

                                setFieldValue("endDate", endDate);
                              }
                            },
                          }}
                        />

                        <Combobox
                          required
                          withForm
                          onChange={(option) => {
                            if (option instanceof Array) {
                              return;
                            }
                            if (
                              option?.value ===
                              REVENUE_SCHEDULE_EXPORT_TYPES.LINE_ITEMS
                            ) {
                              setFieldValue("includeCarryForward", false);
                            }
                          }}
                          name="reportType"
                          label="Group report by"
                          options={convertDisplayToOptions(
                            REVENUE_SCHEDULE_EXPORT_TYPES_DISPLAY
                          )}
                          value={getCurrentDisplayValue({
                            value: values.reportType,
                            options: convertDisplayToOptions(
                              REVENUE_SCHEDULE_EXPORT_TYPES_DISPLAY
                            ),
                          })}
                          menuPortalTarget={document.body}
                        />

                        {values.reportType !==
                          REVENUE_SCHEDULE_EXPORT_TYPES.LINE_ITEMS && (
                          <Checkbox
                            checked={values.includeCarryForward}
                            name="includeCarryForward"
                            onChange={(e) =>
                              setFieldValue(
                                "includeCarryForward",
                                e.target.checked
                              )
                            }
                            label="Include carry-forward from prior months"
                          />
                        )}

                        <Checkbox
                          checked={values.showDeferrals}
                          name="showDeferrals"
                          onChange={(e) =>
                            setFieldValue("showDeferrals", e.target.checked)
                          }
                          label="Include deferrals"
                        />
                      </Modal.Body>
                      <Modal.FooterButtonGroup>
                        <Modal.RawClose asChild>
                          <Button>Cancel</Button>
                        </Modal.RawClose>
                        <Dropdown.Root>
                          <Dropdown.Trigger asChild className="t-group">
                            <span>
                              <Button
                                customType="primary"
                                isLoading={
                                  isExporting ||
                                  isDownloading ||
                                  isFetchingDownload ||
                                  isFetchingExport
                                }
                              >
                                <div className="t-flex t-gap-1 t-items-center">
                                  <span>Export</span>
                                  <div className="group-data-state-open:t-rotate-180 t-ml-2">
                                    <CaretDown />
                                  </div>
                                </div>
                              </Button>
                            </span>
                          </Dropdown.Trigger>
                          <Dropdown.Content
                            align="end"
                            sideOffset={8}
                            side="top"
                          >
                            <ExportOptions
                              // @ts-ignore
                              onLocalDownload={() => onExport(values)}
                              onMailExport={() =>
                                // @ts-ignore
                                onExport({ ...values, exportToMail: true })
                              }
                            />
                          </Dropdown.Content>
                        </Dropdown.Root>
                      </Modal.FooterButtonGroup>
                    </>
                  );
                }}
              </FormikForm>
            </Modal.Content>
          </Modal.Root>
        </div>
      </div>

      <div className="t-overflow-x-auto t-border-neutral-10 t-border-solid t-border t-text-text-60 t-rounded-lg t-overflow-y-hidden">
        <TableUI
          isLoading={isLoadingRevenueSchedule || isFetchingRevenueSchedule}
          selectedCell={activeCellId}
          sizeType="absolute"
          table={table}
          size="small"
          sticky={false}
          header={({ headerGroup, index }) => (
            <Table.Row className="t-bg-surface-lighter-grey">
              {headerGroup.headers.map((header) => {
                return (
                  <Table.HeadCell
                    colSpan={header.colSpan}
                    isUpperCase={false}
                    key={header.id}
                    className={classNames("!t-text-subtext-sm", {
                      "t-drop-shadow-table-left-pin":
                        (values.SHOW_DEFERRALS
                          ? header.column.id === "deferred_total"
                          : header.column.id === "recognized_total") ||
                        header.column.id === "fixed_columns",
                      // Using drop shadow because border collapsing doesn't allow sticky + border to show
                      "t-drop-shadow-border-shadow-neutral-0":
                        showBorderForStickyHeaders(header.column.id),
                      "!t-z-[1]":
                        showBorderForStickyHeaders(header.column.id) &&
                        !header.column.getIsPinned(),
                      "!t-z-[3]":
                        showBorderForStickyHeaders(header.column.id) &&
                        header.column.getIsPinned(),
                    })}
                    style={{
                      width: `${header.getSize()}px`,
                      minWidth: `${header.column.getSize()}px`,
                      ...(index === 0 && header.id === "1_fixed_columns_period"
                        ? {
                            left: 0,
                            position: "sticky",
                            zIndex: 1,
                            backgroundColor: colors.surface["lighter-grey"],
                          }
                        : getCommonPinningStyles(
                            header.column,
                            colors.surface["lighter-grey"],
                            colors.surface["lighter-grey"],
                            2
                          )),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                );
              })}
            </Table.Row>
          )}
        >
          {({ row }) => {
            return (
              <Table.Row
                key={row.id}
                className="last:t-bg-surface-lighter-grey [&:not(:last-child)>td]:!t-bg-white [&:last-child>td]:!t-bg-surface-lighter-grey"
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    className={classNames("!t-text-body-sm", {
                      "!t-drop-shadow-table-left-pin": values.SHOW_DEFERRALS
                        ? cell.column.id === "deferred_total"
                        : cell.column.id === "recognized_total",
                      // Using drop shadow because border collapsing doesn't allow sticky + border to show
                      "t-drop-shadow-border-shadow-neutral-0":
                        showBorderForStickyHeaders(cell.column.id),
                      "!t-z-[1]":
                        showBorderForStickyHeaders(cell.column.id) &&
                        !cell.column.getIsPinned(),
                      "!t-z-[3]":
                        showBorderForStickyHeaders(cell.column.id) &&
                        cell.column.getIsPinned(),
                      "neutral-0-important": cell.id === activeCellId,
                    })}
                    key={cell.id}
                    style={{
                      width: `${cell.column.getSize()}px`,
                      minWidth: `${cell.column.getSize()}px`,
                      ...getCommonPinningStyles(
                        cell.column,
                        colors.surface["lighter-grey"],
                        colors.surface["lighter-grey"],
                        2
                      ),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          }}
        </TableUI>
      </div>
    </div>
  );
};
